/**
 * The drills' arithmetic, apart from their screens (Phase 8, box 4) — so the tests can
 * reach it without a DOM. Nothing here is new analysis: a fresh-shoe position for the
 * chart drill, and the Hi-Lo count of what `composition()` says has left the shoe.
 */
import { BlackjackTable } from "@engine/blackjack/table";
import { DOUBLE_DECK_S17, COMP_SLOTS, freshComposition, rankSlot, SLOT_VALUE } from "@engine/blackjack/rules";
import { botAction } from "@engine/blackjack/bots";
import type { TableSnapshot } from "@engine/blackjack/snapshot";
import { CryptoRng, randomSeed } from "@engine/rng";

const RULES = DOUBLE_DECK_S17;

/** A position with one hero hand to act on, dealt from a fresh two-deck shoe. */
export function freshSpot(): TableSnapshot {
  for (;;) {
    const shoe: number[] = [];
    for (let d = 0; d < RULES.decks; d++) for (let c = 0; c < 52; c++) shoe.push(c);
    const draw = () => shoe.splice(Math.floor(rand() * shoe.length), 1)[0]!;
    const cards = [draw(), draw()];
    const up = draw();
    if (totalOf(cards) === 21) continue;          // a natural is not a decision
    return spotOf(cards, up);
  }
}
export const totalOf = (cards: readonly number[]) => {
  let total = 0, aces = 0;
  for (const c of cards) { const v = SLOT_VALUE[rankSlot(c >> 2)]!; total += v; if (v === 11) aces++; }
  while (total > 21 && aces--) total -= 10;
  return total;
};
export function spotOf(cards: number[], up: number): TableSnapshot {
  return {
    phase: "acting", dealer: [up], dealerNatural: false, turn: { seat: 0, hand: 0 },
    seats: [{ isHero: true, insurance: 0, hands: [{ cards, bet: 1, doubled: false, fromSplit: false, splitAces: false, done: false }] }],
  };
}
/** The drills are practice, not a deal anybody verifies: the browser's own generator. */
export function rand(): number {
  const u = new Uint32Array(1);
  crypto.getRandomValues(u);
  return u[0]! / 2 ** 32;
}
/** Hi-Lo: two to six count one, seven to nine nothing, tens and aces minus one. */
export const HI_LO = (slot: number) => (SLOT_VALUE[slot]! <= 6 ? 1 : SLOT_VALUE[slot]! <= 9 ? 0 : -1);

/**
 * The count of what left the shoe, from the table's own `composition()`: a fresh shoe
 * minus what remains is exactly the cards on the felt, dealer's included once the hand
 * is settled. Exported for the test that checks it against the cards themselves.
 */
export function runningCount(t: BlackjackTable): number {
  const fresh = freshComposition(RULES);
  const left = t.composition();
  let n = 0;
  for (let i = 0; i < COMP_SLOTS; i++) n += (fresh[i]! - left[i]!) * HI_LO(i);
  return n;
}

/** One five-seat hand, played out by the room's bots, settled. */
export function countHand(seed: Uint8Array = randomSeed()): { table: BlackjackTable; cards: number[] } {
  const t = new BlackjackTable({
    rules: RULES, seed, seats: Array.from({ length: 5 }, (_, i) => ({ id: `s${i}`, isHero: i === 0 })),
  });
  const rng = new CryptoRng(seed, "count-drill");
  t.startHand(1);
  if (t.phase === "insurance") { t.seats.forEach((_, i) => t.takeInsurance(i, false)); t.closeInsurance(); }
  for (let guard = 0; t.phase === "acting" && guard < 200; guard++) t.act(botAction(t, "sharp", rng));
  /* Seat by seat, then the dealer's: every card that left the shoe, face up. */
  const cards: number[] = [];
  for (const s of t.seats) for (const h of s.hands) cards.push(...h.cards);
  cards.push(...t.dealer);
  return { table: t, cards };
}

